import {
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useCallback, useEffect, useState } from 'react';
import { UsStateSelect } from '@/components/UsStateSelect';
import {
  fetchDeviceLocationPrefill,
  type DeviceLocationUnavailableReason,
} from '@/lib/sessionLocation/browserDeviceLocation';
import { LOCATION_ATTESTATION_TEXT } from '@/lib/sessionLocation/constants';
import type { LocationPrefillSource } from '@/lib/sessionLocation/types';
import {
  clientLocationConfirmationFormSchema,
  type ClientLocationConfirmationFormInput,
} from '@/schemas/client-location-confirmation.schema';

export interface ClientLocationConfirmationModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirmed: (values: ClientLocationConfirmationFormInput & {
    prefillSource: LocationPrefillSource;
    wasEditedAfterPrefill: boolean;
    ipDerivedCity?: string;
    ipDerivedState?: string;
  }) => Promise<void>;
  savedAddressPrefill?: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
  } | null;
  saving?: boolean;
  saveError?: string | null;
}

const INITIAL_VALUES: ClientLocationConfirmationFormInput = {
  street: '',
  city: '',
  state: '',
  postalCode: '',
  notAtUsualAddress: false,
  attestationAccepted: false,
};

type DeviceLoadState = 'idle' | 'loading' | 'unavailable' | 'found';

function deviceLocationUnavailableMessage(reason: DeviceLocationUnavailableReason | undefined): string {
  switch (reason) {
    case 'permission_denied':
      return 'Location access was denied. Allow location for this site in your browser settings, or enter your address manually.';
    case 'timeout':
      return 'We could not get your device location in time. Please enter your address manually.';
    case 'unsupported':
      return 'Your browser does not support device location. Please enter your address manually.';
    case 'non_us':
      return 'Your device location appears to be outside the United States. Please enter the US address where you are for today\u2019s session.';
    case 'incomplete':
      return 'We could not determine a complete US address from your device location. Please enter or edit the address below.';
    case 'reverse_geocode_failed':
    case 'position_unavailable':
    default:
      return 'We could not determine your location from this device. Please enter the address where you are physically located for today\u2019s session.';
  }
}

export function ClientLocationConfirmationModal({
  opened,
  onClose,
  onConfirmed,
  savedAddressPrefill,
  saving = false,
  saveError,
}: ClientLocationConfirmationModalProps): JSX.Element {
  const [deviceLoadState, setDeviceLoadState] = useState<DeviceLoadState>('idle');
  const [devicePrefillComplete, setDevicePrefillComplete] = useState(true);
  const [deviceUnavailableReason, setDeviceUnavailableReason] = useState<
    DeviceLocationUnavailableReason | undefined
  >();
  const [derivedCity, setDerivedCity] = useState<string | undefined>();
  const [derivedState, setDerivedState] = useState<string | undefined>();
  const [prefillSource, setPrefillSource] = useState<LocationPrefillSource>('none');
  const [userEdited, setUserEdited] = useState(false);
  const [prefillSnapshot, setPrefillSnapshot] = useState('');
  const [entryMode, setEntryMode] = useState<'choose' | 'manual' | 'device'>('choose');

  const form = useForm<ClientLocationConfirmationFormInput>({
    initialValues: INITIAL_VALUES,
    validate: zod4Resolver(clientLocationConfirmationFormSchema),
  });

  const applyPrefill = useCallback(
    (values: Partial<ClientLocationConfirmationFormInput>, source: LocationPrefillSource) => {
      const next = {
        street: values.street ?? '',
        city: values.city ?? '',
        state: values.state ?? '',
        postalCode: values.postalCode ?? '',
      };
      form.setValues({
        ...form.values,
        ...next,
        notAtUsualAddress: form.values.notAtUsualAddress,
        attestationAccepted: form.values.attestationAccepted,
      });
      setPrefillSource(source);
      setUserEdited(false);
      setPrefillSnapshot(JSON.stringify(next));
    },
    [form],
  );

  const loadDeviceLocation = useCallback(async () => {
    setEntryMode('device');
    setDeviceLoadState('loading');
    setDeviceUnavailableReason(undefined);
    const result = await fetchDeviceLocationPrefill();
    if (!result.available) {
      setDeviceLoadState('unavailable');
      setDeviceUnavailableReason(result.unavailableReason);
      setDevicePrefillComplete(true);
      return;
    }
    setDerivedCity(result.derivedCity);
    setDerivedState(result.derivedState);
    setDevicePrefillComplete(result.isComplete);
    applyPrefill(
      {
        street: result.street,
        city: result.city,
        state: result.state,
        postalCode: result.postalCode,
      },
      'device_location',
    );
    setDeviceLoadState('found');
  }, [applyPrefill]);

  useEffect(() => {
    if (!opened) {
      form.reset();
      setDeviceLoadState('idle');
      setDeviceUnavailableReason(undefined);
      setDevicePrefillComplete(true);
      setDerivedCity(undefined);
      setDerivedState(undefined);
      setPrefillSource('none');
      setUserEdited(false);
      setPrefillSnapshot('');
      setEntryMode('choose');
      return;
    }
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = useCallback(
    (field: keyof ClientLocationConfirmationFormInput, value: string | boolean) => {
      if (entryMode === 'choose') {
        setEntryMode('manual');
        setPrefillSource('none');
      }
      form.setFieldValue(field, value as never);
      if (prefillSnapshot) {
        const current = JSON.stringify({
          street: field === 'street' ? value : form.values.street,
          city: field === 'city' ? value : form.values.city,
          state: field === 'state' ? value : form.values.state,
          postalCode: field === 'postalCode' ? value : form.values.postalCode,
        });
        if (current !== prefillSnapshot) {
          setUserEdited(true);
        }
      } else if (entryMode === 'manual') {
        setUserEdited(true);
      }
    },
    [form, prefillSnapshot, entryMode],
  );

  const handleSubmit = form.onSubmit(async (values) => {
    await onConfirmed({
      ...values,
      prefillSource,
      wasEditedAfterPrefill: userEdited,
      ipDerivedCity: derivedCity,
      ipDerivedState: derivedState,
    });
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Confirm your location for today's session"
      centered
      size="md"
      closeOnClickOutside={false}
      closeOnEscape={!saving}
      withCloseButton={!saving}
    >
      <Stack gap="md">
        {entryMode === 'choose' ? (
          <>
            <Text size="sm" c="gray.7">
              Before joining, confirm where you are physically located for today&apos;s session.
              You can use your device location or enter your address manually.
            </Text>
            <Stack gap="xs">
              <Button
                type="button"
                variant="light"
                disabled={saving}
                onClick={() => void loadDeviceLocation()}
              >
                Use my device location
              </Button>
              <Button
                type="button"
                variant="default"
                disabled={saving}
                onClick={() => {
                  setEntryMode('manual');
                  setPrefillSource('none');
                }}
              >
                Enter address manually
              </Button>
            </Stack>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              {entryMode === 'device' && deviceLoadState === 'loading' ? (
                <Group gap="xs">
                  <Loader size="xs" />
                  <Text size="sm" c="dimmed">
                    Getting your location… Your browser may ask for permission.
                  </Text>
                </Group>
              ) : null}

              {entryMode === 'device' && deviceLoadState === 'found' && devicePrefillComplete ? (
                <Text size="sm" c="gray.7">
                  We prefilled an address from your device location. GPS and Wi‑Fi estimates can be
                  imprecise indoors. Please confirm or edit the address where you are physically
                  located right now.
                </Text>
              ) : null}

              {entryMode === 'device' && deviceLoadState === 'found' && !devicePrefillComplete ? (
                <Alert color="yellow" variant="light">
                  We found your city and state from your device location but could not determine a
                  complete street address or ZIP. Please complete the fields below.
                </Alert>
              ) : null}

              {entryMode === 'device' && deviceLoadState === 'unavailable' ? (
                <Alert color="yellow" variant="light">
                  {deviceLocationUnavailableMessage(deviceUnavailableReason)}
                </Alert>
              ) : null}

              {entryMode === 'manual' ? (
                <Text size="sm" c="gray.7">
                  Enter the address where you are physically located for today&apos;s session.
                </Text>
              ) : null}

              <Group gap="xs" wrap="wrap">
                {entryMode !== 'device' ? (
                  <Button
                    type="button"
                    variant="subtle"
                    size="xs"
                    disabled={saving || deviceLoadState === 'loading'}
                    onClick={() => void loadDeviceLocation()}
                  >
                    Use my device location
                  </Button>
                ) : null}
                {savedAddressPrefill ? (
                  <Button
                    type="button"
                    variant="subtle"
                    size="xs"
                    disabled={saving}
                    onClick={() => {
                      setEntryMode('manual');
                      applyPrefill(
                        {
                          street: savedAddressPrefill.street,
                          city: savedAddressPrefill.city,
                          state: savedAddressPrefill.state,
                          postalCode: savedAddressPrefill.postalCode,
                        },
                        'saved_address',
                      );
                    }}
                  >
                    Autofill saved address
                  </Button>
                ) : null}
              </Group>

              <TextInput
                label="Street address"
                required
                disabled={saving}
                value={form.values.street}
                onChange={(e) => handleFieldChange('street', e.currentTarget.value)}
                error={form.errors.street}
              />
              <TextInput
                label="City"
                required
                disabled={saving}
                value={form.values.city}
                onChange={(e) => handleFieldChange('city', e.currentTarget.value)}
                error={form.errors.city}
              />
              <UsStateSelect
                label="State"
                required
                disabled={saving}
                value={form.values.state}
                onChange={(v) => handleFieldChange('state', v)}
                error={form.errors.state}
              />
              <TextInput
                label="ZIP code"
                required
                disabled={saving}
                value={form.values.postalCode}
                onChange={(e) => handleFieldChange('postalCode', e.currentTarget.value)}
                error={form.errors.postalCode}
              />

              <Checkbox
                label="I am not at my usual address"
                disabled={saving}
                checked={form.values.notAtUsualAddress}
                onChange={(e) => form.setFieldValue('notAtUsualAddress', e.currentTarget.checked)}
              />

              <Checkbox
                label={LOCATION_ATTESTATION_TEXT}
                disabled={saving}
                checked={form.values.attestationAccepted}
                onChange={(e) =>
                  form.setFieldValue('attestationAccepted', e.currentTarget.checked)
                }
                error={form.errors.attestationAccepted}
              />

              {saveError ? (
                <Alert color="red" variant="light">
                  {saveError}
                </Alert>
              ) : null}

              <Group justify="flex-end">
                <Button variant="default" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" loading={saving}>
                  Confirm and Join Session
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Stack>
    </Modal>
  );
}
